<script setup lang="ts">
  import { ref, computed, onMounted, watch } from 'vue';
  import { DatePicker, FilterModal, IconImage, SearchCombobox, ThemeProvider } from '@/shared/ui';
  import CalendarIcon from '@/shared/assets/icons/CalendarIcon.svg';
  import { formatDateForApi, formatDateToPtBR, parseCivilDate } from '@/shared/lib';
  import { usePermittedMembers } from '@/entities/User';
  import { positionApi, type Position } from '@/entities/Position';
  import {
    MEMBER_TRAINING_STATUS_LABELS,
    TRAINING_TYPE_OPTIONS,
    type TrainingListFilters,
    type TrainingListInstructorFilter,
    type TrainingStatus,
    type TrainingType,
  } from '@/entities/Training';

  const INSTRUCTOR_OPTIONS: { value: TrainingListInstructorFilter; label: string }[] = [
    { value: 'INTERNAL', label: 'Interno' },
    { value: 'EXTERNAL', label: 'Externo' },
  ];

  const CERTIFICATE_OPTIONS: { value: boolean; label: string }[] = [
    { value: true, label: 'Com certificado' },
    { value: false, label: 'Sem certificado' },
  ];

  const STATUS_OPTIONS: { value: TrainingStatus; label: string }[] = [
    { value: 'PENDING', label: MEMBER_TRAINING_STATUS_LABELS.PENDING },
    { value: 'COMPLETED', label: MEMBER_TRAINING_STATUS_LABELS.COMPLETED },
  ];

  const props = defineProps<{
    currentFilters?: TrainingListFilters;
  }>();

  const emit = defineEmits<{
    close: [];
    'update:filter': [filters: TrainingListFilters];
    'clear-filters': [];
  }>();

  const {
    userOptions: memberOptions,
    isLoading: isMembersLoading,
    fetchUsers,
  } = usePermittedMembers({ includeInactiveMembers: true });

  const positions = ref<Position[]>([]);
  const isPositionsLoading = ref(false);

  const memberSearchQuery = ref('');
  const positionSearchQuery = ref('');

  const draftType = ref<TrainingType | undefined>(undefined);
  const draftInstructor = ref<TrainingListInstructorFilter | undefined>(undefined);
  const draftCertificateRequired = ref<boolean | undefined>(undefined);
  const draftStatus = ref<TrainingStatus | undefined>(undefined);
  const draftMemberId = ref('');
  const draftPositionId = ref('');
  const draftTrainingDate = ref<Date | null>(null);
  const isTrainingDateOpen = ref(false);

  const memberComboboxOptions = computed(() => {
    const query = memberSearchQuery.value.trim().toLowerCase();
    return memberOptions.value
      .filter((member) => !query || member.name.toLowerCase().includes(query))
      .map((member) => ({ value: String(member.id), label: member.name }));
  });

  const positionComboboxOptions = computed(() => {
    const query = positionSearchQuery.value.trim().toLowerCase();
    return positions.value
      .filter((position) => !query || position.name.toLowerCase().includes(query))
      .map((position) => ({ value: String(position.id), label: position.name }));
  });

  const trainingDateLabel = computed(() =>
    draftTrainingDate.value ? formatDateToPtBR(draftTrainingDate.value) : 'Selecione uma data'
  );

  function loadFromFilters(filters?: TrainingListFilters) {
    draftType.value = filters?.type;
    draftInstructor.value = filters?.instructor;
    draftCertificateRequired.value = filters?.certificateRequired;
    draftStatus.value = filters?.status;
    draftMemberId.value = filters?.memberId ? String(filters.memberId) : '';
    draftPositionId.value = filters?.positionId ? String(filters.positionId) : '';
    draftTrainingDate.value = parseCivilDate(filters?.trainingDate);
  }

  async function loadPositions() {
    isPositionsLoading.value = true;
    try {
      const response = await positionApi.getAllPositions({ itemsPerPage: 1000 });
      positions.value = response.data || [];
    } catch (error) {
      console.error('Erro ao carregar cargos:', error);
    } finally {
      isPositionsLoading.value = false;
    }
  }

  onMounted(async () => {
    await Promise.all([loadPositions(), fetchUsers()]);
    loadFromFilters(props.currentFilters);
  });

  watch(
    () => props.currentFilters,
    (filters) => loadFromFilters(filters),
    { deep: true }
  );

  function toggleType(value: TrainingType) {
    draftType.value = draftType.value === value ? undefined : value;
  }

  function toggleInstructor(value: TrainingListInstructorFilter) {
    draftInstructor.value = draftInstructor.value === value ? undefined : value;
  }

  function toggleCertificateRequired(value: boolean) {
    draftCertificateRequired.value = draftCertificateRequired.value === value ? undefined : value;
  }

  function toggleStatus(value: TrainingStatus) {
    draftStatus.value = draftStatus.value === value ? undefined : value;
  }

  function handleApply() {
    emit('update:filter', {
      type: draftType.value,
      instructor: draftInstructor.value,
      certificateRequired: draftCertificateRequired.value,
      status: draftStatus.value,
      memberId: draftMemberId.value ? Number(draftMemberId.value) : undefined,
      positionId: draftPositionId.value ? Number(draftPositionId.value) : undefined,
      trainingDate: draftTrainingDate.value
        ? (formatDateForApi(draftTrainingDate.value) ?? undefined)
        : undefined,
    });
  }

  function handleClear() {
    loadFromFilters(undefined);
    memberSearchQuery.value = '';
    positionSearchQuery.value = '';
    emit('update:filter', {});
  }
</script>

<template>
  <FilterModal
    content-class="w-[593px]"
    @close="emit('close')"
    @apply-filters="handleApply"
    @clear-filters="handleClear"
  >
    <template #content>
      <ThemeProvider class="overflow-auto">
        <div class="flex gap-6 items-start p-6 border-b border-gray-02">
          <div class="flex flex-col gap-4 items-start w-[273px] shrink-0">
            <div>
              <h4 class="text-sm font-medium text-gray-11 pb-4">Tipo de Treinamento</h4>
              <div
                data-testid="training-list-type-options"
                class="flex flex-col items-start gap-1 pb-3"
              >
                <button
                  v-for="option in TRAINING_TYPE_OPTIONS"
                  :key="option.value"
                  type="button"
                  :data-testid="`training-list-type-${option.value}`"
                  class="flex items-center justify-start py-2 px-4 rounded-lg w-full text-sm text-left"
                  :class="
                    draftType === option.value ? 'bg-(--theme-secondary) text-white' : 'text-gray-07'
                  "
                  @click="toggleType(option.value)"
                >
                  {{ option.label }}
                </button>
              </div>
            </div>

            <div>
              <h4 class="text-sm font-medium text-gray-11 pb-4">Ministrante</h4>
              <div
                data-testid="training-list-instructor-options"
                class="flex flex-col items-start gap-1 pb-3"
              >
                <button
                  v-for="option in INSTRUCTOR_OPTIONS"
                  :key="option.value"
                  type="button"
                  :data-testid="`training-list-instructor-${option.value}`"
                  class="flex items-center justify-start py-2 px-4 rounded-lg w-full text-sm text-left"
                  :class="
                    draftInstructor === option.value
                      ? 'bg-(--theme-secondary) text-white'
                      : 'text-gray-07'
                  "
                  @click="toggleInstructor(option.value)"
                >
                  {{ option.label }}
                </button>
              </div>
            </div>
          </div>

          <div class="flex flex-col gap-2 items-start">
            <div class="w-[236px]">
              <h4 class="text-sm font-medium text-gray-11 pb-4">Certificado</h4>
              <div
                data-testid="training-list-certificate-options"
                class="flex flex-col items-start gap-1 pb-3"
              >
                <button
                  v-for="option in CERTIFICATE_OPTIONS"
                  :key="String(option.value)"
                  type="button"
                  :data-testid="`training-list-certificate-${option.value}`"
                  class="flex items-center justify-start py-2 px-4 rounded-lg w-full text-sm text-left"
                  :class="
                    draftCertificateRequired === option.value
                      ? 'bg-(--theme-secondary) text-white'
                      : 'text-gray-07'
                  "
                  @click="toggleCertificateRequired(option.value)"
                >
                  {{ option.label }}
                </button>
              </div>
            </div>

            <div>
              <h4 class="text-sm font-medium text-gray-11 pb-4">Status</h4>
              <div
                data-testid="training-list-status-options"
                class="flex flex-col items-start gap-1 pb-3"
              >
                <button
                  v-for="option in STATUS_OPTIONS"
                  :key="option.value"
                  type="button"
                  :data-testid="`training-list-status-${option.value}`"
                  class="flex items-center justify-start py-2 px-4 rounded-lg w-full text-sm text-left"
                  :class="
                    draftStatus === option.value ? 'bg-(--theme-secondary) text-white' : 'text-gray-07'
                  "
                  @click="toggleStatus(option.value)"
                >
                  {{ option.label }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="flex flex-col items-start pt-4 px-6 pb-6 border-b border-gray-02">
          <h4 class="text-sm font-medium text-gray-11 h-8 flex items-center">Colaboradores</h4>
          <SearchCombobox
            :model-value="draftMemberId"
            :options="memberComboboxOptions"
            :loading="isMembersLoading"
            placeholder="Selecione um colaborador"
            search-placeholder="Buscar colaborador..."
            trigger-icon="arrow-down"
            class="w-full h-11"
            @update:model-value="draftMemberId = $event || ''"
            @search="memberSearchQuery = $event"
          />
        </div>

        <div class="flex flex-col items-start pt-4 px-6 pb-6 border-b border-gray-02">
          <h4 class="text-sm font-medium text-gray-11 h-8 flex items-center">Cargo</h4>
          <SearchCombobox
            :model-value="draftPositionId"
            :options="positionComboboxOptions"
            :loading="isPositionsLoading"
            placeholder="Selecione um cargo"
            search-placeholder="Buscar cargo..."
            trigger-icon="arrow-down"
            class="w-full h-11"
            @update:model-value="draftPositionId = $event || ''"
            @search="positionSearchQuery = $event"
          />
        </div>

        <div class="flex flex-col items-start pt-4 px-6 pb-6 border-b border-gray-02">
          <h4 class="text-sm font-medium text-gray-11 h-8 flex items-center">Data do Treinamento</h4>
          <button
            type="button"
            data-testid="training-list-training-date-trigger"
            class="flex w-[182px] items-center gap-2 bg-white-01 px-4 py-2.5 border border-gray-02 rounded-lg text-sm text-gray-03 focus:outline-none focus:border-(--theme-secondary-ink) font-medium"
            @click="isTrainingDateOpen = true"
          >
            <IconImage :src="CalendarIcon" alt="Calendário" class="h-5 w-5 shrink-0" />
            <span class="truncate text-left">{{ trainingDateLabel }}</span>
          </button>
          <DatePicker
            v-model:selected-date="draftTrainingDate"
            v-model:is-open="isTrainingDateOpen"
            @apply="isTrainingDateOpen = false"
            @cancel="isTrainingDateOpen = false"
          />
        </div>
      </ThemeProvider>
    </template>
  </FilterModal>
</template>
